import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown, Link2, MessageSquare, RefreshCw, Send } from "lucide-react";
import siteUrl from "./../../../../const/const";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import api from "../../../../const/axios";
import StarRating from "../../../Elements/StarRating";
import { isFeatureEnabled } from "../../../../services/flagsService";
import { DiscussionComment, useDiscussion } from "../DiscussionContext";

interface QuestionValue {
  id: number;
  text: string;
  answer: string;
  rekvizit: { text: boolean; rekvizit: string } | null;
}

interface QuestionData {
  values: QuestionValue[];
  isRekvizitForAll?: boolean;
  notebefore?: string;
  considered?: string;
  comment?: string;
  sources: string[];
  authors: { fullname: string }[];
}

interface QuestionProps {
  question: QuestionData;
  index: number;
  opens: number[];
  toggleOpen: (id: number) => void;
  commentsOpen: boolean;
  onToggleComments: () => void;
}

const renderQuestionType = (values: QuestionValue[], index: number) => {
  switch (values.length) {
    case 2:
      return <>{index + 1}. <span className="font-bold text-blue-500 dark:text-indigo-300"> Duplet</span></>;
    case 3:
      return <>{index + 1}. <span className="font-bold text-blue-500 dark:text-indigo-300"> Blits</span></>;
    case 4:
      return <>{index + 1}. <span className="font-bold text-blue-500 dark:text-indigo-300"> Kvadriplet</span></>;
    default:
      return null;
  }
};

const TextLines: React.FC<{ text: string; indent?: boolean }> = ({ text, indent }) =>
  <>
    {text.split("/-/").map((m, i) => (
      <span key={i}>
        {indent && i ? <span>&nbsp;&nbsp;</span> : null}
        {m}
        <br />
      </span>
    ))}
  </>;

const Question: React.FC<QuestionProps> = ({
  question: q,
  index,
  opens,
  toggleOpen,
  commentsOpen,
  onToggleComments,
}) => {
  const { t } = useTranslation();
  const isOpen = opens.includes(q.values[0].id);
  const isMulti = q.values.length > 1;
  const hasBefore = Boolean(q.notebefore);
  let keyused = false;

  const questionId = q.values[0].id;

  const userData = useSelector((state: any) => state.user);
  const isLoggedIn: boolean = userData?.isLoggedIn ?? false;
  const currentUser = userData?.data ?? {};
  const isVerified: boolean = currentUser?.isVerified ?? false;

  const { entry: discussion, loadComments, setEntry: setDiscussionEntry } = useDiscussion(questionId);

  const comments: DiscussionComment[] = useMemo(() => discussion?.comments ?? [], [discussion?.comments]);
  const commentsLoaded = discussion?.comments != null;
  const commentCount: number = commentsLoaded ? comments.length : discussion?.commentCount ?? 0;
  const avgRating: number = discussion?.average ?? 0;
  const ratingCount: number = discussion?.count ?? 0;

  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentNotice, setCommentNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSort, setCommentSort] = useState<"newest" | "oldest">("newest");
  const [myRating, setMyRating] = useState<number | null>(null);
  const [rating, setRating] = useState(false);
  const commentsListRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const ratingStorageKey = useMemo(() => {
    const userId = currentUser?.id ?? "guest";
    return `rated_questions_${userId}`;
  }, [currentUser?.id]);

  const getRatingMap = (): Record<string, number> => {
    try {
      const raw = localStorage.getItem(ratingStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const setRatingMap = (next: Record<string, number>) => {
    try {
      localStorage.setItem(ratingStorageKey, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const commentDraftStorageKey = useMemo(() => `question_comment_draft_${questionId}`, [questionId]);

  const refreshComments = useCallback(async (silent = false) => {
    if (!silent) setCommentsLoading(true);
    try {
      await loadComments();
    } finally {
      if (!silent) setCommentsLoading(false);
    }
  }, [loadComments]);

  // Comments are fetched the first time the panel is opened, not with the package.
  useEffect(() => {
    if (commentsOpen && !commentsLoaded) refreshComments();
  }, [commentsOpen, commentsLoaded, refreshComments]);

  const normalizedCommentText = commentText.trim();
  const questionPreview = useMemo(() => {
    const raw = q.values?.[0]?.text ?? "";
    return raw.replace(/\/-\//g, " ").replace(/\s+/g, " ").trim().slice(0, 180);
  }, [q.values]);

  const sortedComments = useMemo(() => {
    const copied = [...comments];
    copied.sort((a, b) => {
      const av = new Date(a.added).getTime();
      const bv = new Date(b.added).getTime();
      return commentSort === "newest" ? bv - av : av - bv;
    });
    return copied;
  }, [comments, commentSort]);

  const getCommentAuthorName = (comment: DiscussionComment) =>
    `${comment.user?.name ?? ""} ${comment.user?.surname ?? ""}`.trim() ||
    t("comments.anonymous", "İstifadəçi");

  const getCommentAvatarInitials = (comment: DiscussionComment) => {
    const label = getCommentAuthorName(comment);
    const parts = label.split(" ").filter(Boolean);
    if (!parts.length) return "İ";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
  };

  const formatCommentDate = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("az-AZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (discussion?.myRating != null) {
      setMyRating(discussion.myRating);
    } else {
      const stored = getRatingMap()[String(questionId)];
      setMyRating(stored ?? null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, discussion?.myRating]);

  useEffect(() => {
    try {
      const draft = localStorage.getItem(commentDraftStorageKey);
      setCommentText(draft ?? "");
    } catch {
      setCommentText("");
    }
  }, [commentDraftStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(commentDraftStorageKey, commentText);
    } catch {
      // ignore storage errors
    }
  }, [commentDraftStorageKey, commentText]);

  useEffect(() => {
    if (!commentsOpen) return;
    const tid = window.setTimeout(() => textareaRef.current?.focus(), 80);
    return () => window.clearTimeout(tid);
  }, [commentsOpen]);

  useEffect(() => {
    if (commentsOpen) return;
    setCommentError(null);
    setCommentNotice(null);
  }, [commentsOpen]);

  const submitComment = async () => {
    if (!normalizedCommentText) return;
    setPosting(true);
    setCommentError(null);
    setCommentNotice(null);
    try {
      const res = await api.post(`questions/${questionId}/comments`, { text: normalizedCommentText });
      if (res.data?.comment) {
        setDiscussionEntry({
          comments: [...comments, res.data.comment],
          commentCount: commentCount + 1,
        });
        setCommentNotice(t("comments.sent", "Şərh əlavə edildi."));
      } else if (res.data?.message) {
        setCommentNotice(res.data.message);
      }
      setCommentText("");
      const list = commentsListRef.current;
      if (list) {
        const shouldScrollToTop = commentSort === "newest";
        requestAnimationFrame(() => {
          list.scrollTo({
            top: shouldScrollToTop ? 0 : list.scrollHeight,
            behavior: "smooth",
          });
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? t("comments.error");
      setCommentError(msg);
    } finally {
      setPosting(false);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/questions/${questionId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const submitRating = async (value: number) => {
    if (rating) return;
    setRating(true);
    const prevRating = myRating;
    const prevAvg = avgRating;
    const prevCount = ratingCount;

    setMyRating(value);
    setRatingMap({ ...getRatingMap(), [String(questionId)]: value });

    try {
      const res = await api.post(`questions/${questionId}/rate`, { rating: value });
      setDiscussionEntry({
        average: res.data?.average ?? prevAvg,
        count: res.data?.count ?? prevCount,
        myRating: value,
      });
    } catch {
      setMyRating(prevRating);
      setDiscussionEntry({
        average: prevAvg,
        count: prevCount,
        myRating: prevRating,
      });
    } finally {
      setRating(false);
    }
  };

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4 last:border-0">
      {/* Question type badge (Duplet, Blits, etc.) */}
      {isMulti && (
        <div className="mb-1 text-base">
          {renderQuestionType(q.values, index)}
          {(keyused = true) && null}
        </div>
      )}

      {/* Shared rekvizit (for-all) */}
      {q.isRekvizitForAll && (
        <div className="mb-2">
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Paylama materialı</p>
          {q.values[0].rekvizit?.text ? (
            <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
              <TextLines text={q.values[0].rekvizit.rekvizit} />
            </p>
          ) : q.values[0].rekvizit ? (
            <img
              src={`${siteUrl}images/${q.values[0].rekvizit.rekvizit}`}
              alt={q.values[0].rekvizit.rekvizit}
              className="max-w-full max-h-40 rounded-lg"
            />
          ) : null}
        </div>
      )}

      {/* Note before */}
      {q.notebefore && (
        <p className="text-gray-800 dark:text-gray-100 font-semibold mb-1">
          {keyused ? "" : `${index + 1}. `}
          {q.notebefore}
        </p>
      )}

      {/* Question values */}
      <div className="space-y-1">
        {q.values.map((qv, i) => {
          const rekOrNot = qv.rekvizit !== null;
          const rekTextOrNot = rekOrNot ? qv.rekvizit!.text : false;
          const mainKey = index + 1;
          const key = i + 1;

          return (
            <div key={i} className="text-gray-800 dark:text-gray-100 font-semibold break-words">
              {isMulti ? (
                rekOrNot && !q.isRekvizitForAll ? (
                  <p>{key}. Rekvizit</p>
                ) : (
                  <p>{key}. <TextLines text={qv.text} indent /></p>
                )
              ) : (
                !hasBefore ? (
                  rekOrNot ? (
                    <p>{mainKey}. <span className="font-bold">Paylama materialı</span></p>
                  ) : (
                    <p>{mainKey}. <TextLines text={qv.text} indent /></p>
                  )
                ) : (
                  rekOrNot ? (
                    <p>Paylama materialı</p>
                  ) : (
                    <p><TextLines text={qv.text} /></p>
                  )
                )
              )}

              {rekTextOrNot && !q.isRekvizitForAll && (
                <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm mt-1">
                  <TextLines text={qv.rekvizit!.rekvizit} />
                </p>
              )}

              {rekOrNot && !q.isRekvizitForAll && !rekTextOrNot && (
                <img
                  src={`${siteUrl}images/${qv.rekvizit!.rekvizit}`}
                  alt={qv.rekvizit!.rekvizit}
                  className="max-w-full max-h-40 rounded-lg mt-1"
                />
              )}

              {rekOrNot && !q.isRekvizitForAll && (
                <p className="text-gray-800 dark:text-gray-100 font-semibold">
                  <TextLines text={qv.text} />
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Reveal toggle */}
      <button
        onClick={() => toggleOpen(q.values[0].id)}
        className="flex items-center gap-1 mt-2 text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
        aria-label="Cavabı göstər / gizlət"
      >
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform duration-300",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isFeatureEnabled('QUESTION_ENGAGEMENT_BAR') && (
      <div className="mt-2 flex items-center gap-2 text-xs">
        <StarRating
          average={avgRating}
          count={ratingCount}
          myRating={myRating}
          onRate={submitRating}
          disabled={rating}
          loading={rating}
          canRate={isLoggedIn && isVerified}
        />
        {((isLoggedIn && isVerified) || ratingCount !== 0) && <span className="text-gray-300">•</span>}
        <button
          onClick={onToggleComments}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          aria-label="Şərhləri aç"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {t("comments.title", "Şərhlər")} ({commentCount})
        </button>
        <span className="text-gray-300">•</span>
        <button
          onClick={copyShareLink}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          aria-label="Linki kopyala"
        >
          <Link2 className="h-3.5 w-3.5" />
          {copied ? "Kopyalandı!" : "Paylaş"}
        </button>
      </div>
      )}

      {/* Answer / considered / comment / sources / authors */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300",
          isOpen ? "max-h-[2000px] opacity-100 mt-2" : "max-h-0 opacity-0"
        )}
      >
        <div className="space-y-1.5 text-sm">
          {q.values && (
            <p className="text-gray-700 dark:text-gray-200 font-medium">
              <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
              {q.values.length > 1
                ? `1. ${q.values[0].answer}`
                : q.values[0].answer}
              {q.values[1] ? ` 2. ${q.values[1].answer}` : null}
              {q.values[2] ? ` 3. ${q.values[2].answer}` : null}
              {q.values[3] ? ` 4. ${q.values[3].answer}` : null}
            </p>
          )}
          {q.considered && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Sayılma meyarı: </span>
              {q.considered}
            </p>
          )}
          {q.comment && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
              {q.comment}
            </p>
          )}
          {q.sources.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200 break-all">
              <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
              {q.sources.map((src, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                  {q.sources.length > 1 ? `${i + 1}. ${src}` : src}
                </React.Fragment>
              ))}
            </p>
          )}
          {q.authors?.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">
                {q.authors.length > 1 ? t("packages.authorPlural") : t("packages.authorSingular")}
              </span>{" "}
              {q.authors.map((a, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? ", " : ""}
                  {a.fullname}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>

      {/* Comments subsection */}
      <div
        className={cn(
          // Side padding (offset by the negative margin) leaves room for the textarea focus ring,
          // which overflow-hidden would otherwise clip.
          "-mx-1 px-1 overflow-hidden transition-all duration-300",
          commentsOpen ? "max-h-[900px] opacity-100 mt-3" : "max-h-0 opacity-0"
        )}
      >
          <div className="rounded-xl bg-white dark:bg-gray-900">
            <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 py-3 gap-4">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Sual üzrə müzakirə</h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                  #{index + 1} {questionPreview || "Sual mətni"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => refreshComments()}
                  disabled={commentsLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  title="Yenilə"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", commentsLoading && "animate-spin")} />
                  Yenilə
                </button>
                <button
                  onClick={() => setCommentSort((prev) => (prev === "newest" ? "oldest" : "newest"))}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  title="Sıralamanı dəyiş"
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  {commentSort === "newest" ? "Ən yeni" : "Ən köhnə"}
                </button>
              </div>
            </div>

            <div ref={commentsListRef} className="py-3 overflow-y-auto max-h-64">
              {commentsLoading ? (
                <ul className="space-y-3">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <li key={idx} className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3 animate-pulse">
                      <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
                      <div className="mt-2 h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                      <div className="mt-1 h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </li>
                  ))}
                </ul>
              ) : sortedComments.length > 0 ? (
                <ul className="space-y-3 mb-2">
                  {sortedComments.map((c) => {
                    const isMine = Boolean(currentUser?.id) && c.user?.id === currentUser?.id;
                    return (
                      <li
                        key={c.id}
                        className={cn(
                          "rounded-xl border p-3 text-sm",
                          isMine ? "border-blue-200 bg-blue-50/60 dark:border-indigo-900 dark:bg-indigo-950/40" : "border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                            {getCommentAvatarInitials(c)}
                          </div>
                          <span className="font-medium text-gray-800 dark:text-gray-100">{getCommentAuthorName(c)}</span>
                          {isMine && (
                            <span className="rounded-full bg-blue-100 dark:bg-indigo-900/60 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-indigo-300">
                              Siz
                            </span>
                          )}
                          <span className="text-gray-400 dark:text-gray-500 text-xs">{formatCommentDate(c.added)}</span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-200 mt-2 break-words whitespace-pre-wrap">{c.text}</p>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 py-8 text-center">
                  <MessageSquare className="mx-auto h-7 w-7 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t("comments.empty", "Hələ şərh yoxdur.")}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Müzakirəyə ilk şərhlə başlayın.</p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 py-3 bg-white dark:bg-gray-900">
              {isLoggedIn && isVerified ? (
                <div className="flex flex-col gap-1.5">
                  <textarea
                    ref={textareaRef}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !posting && normalizedCommentText) {
                        e.preventDefault();
                        submitComment();
                      }
                    }}
                    placeholder={t("comments.placeholder", "Şərhinizi yazın...")}
                    rows={3}
                    maxLength={1000}
                    className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  />
                  <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                    <span>Göndərmək üçün Ctrl+Enter istifadə edin</span>
                    <span>{commentText.length}/1000</span>
                  </div>
                  {commentError && <p className="text-xs text-red-500">{commentError}</p>}
                  {commentNotice && <p className="text-xs text-emerald-600">{commentNotice}</p>}
                  <button
                    onClick={submitComment}
                    disabled={posting || !normalizedCommentText}
                    className="self-end inline-flex items-center gap-1.5 text-xs bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {posting ? t("comments.sending", "Göndərilir...") : t("comments.send", "Göndər")}
                  </button>
                </div>
              ) : isLoggedIn && !isVerified ? (
                <p className="text-xs text-amber-600">{t("comments.notVerified", "Şərh yazmaq üçün hesabınızın təsdiqlənməsi lazımdır.")}</p>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500">{t("comments.loginRequired", "Şərh yazmaq üçün daxil olun.")}</p>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Question;
